import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Calendar, Clock, ImageOff, NotebookPen } from 'lucide-react';
import API from '../../utils/api';
import { useFailedImages } from '../../hooks/useFailedImages';
import { blogImage, formatBlogDate, readMinutes } from '../Blog/blogUtils';
import './BlogSection.css';

// Home page: the three newest published posts, laid out like a magazine (one featured, two beside it).
// Hidden when there are none.
const BlogSection = () => {
    const [posts, setPosts] = useState([]);
    const [failed, markFailed] = useFailedImages();

    useEffect(() => {
        let cancelled = false;
        API.get('/blogs', { params: { limit: 3 } })
            .then(res => { if (!cancelled) setPosts(res.data.blogs || []); })
            .catch(() => { if (!cancelled) setPosts([]); });
        return () => { cancelled = true; };
    }, []);

    if (posts.length === 0) return null;

    const [featured, ...others] = posts;

    const Media = ({ post, className }) => {
        const image = blogImage(post.coverImage);
        const hasImage = image && !failed.has(image);
        return (
            <div className={`hbl-media ${className || ''} ${hasImage ? '' : 'hbl-art'}`}>
                {hasImage
                    ? <img src={image} alt={`Cover image: ${post.title}`} loading="lazy" onError={() => markFailed(image)} />
                    : <><span className="hbl-art__icon"><ImageOff size={32} strokeWidth={1.5} /></span><b>Cover unavailable</b></>}
            </div>
        );
    };

    return (
        <section className="hbl" aria-labelledby="hbl-title">
            <div className="hbl__inner">
                <header className="hbl__head">
                    <span className="hbl__kicker"><NotebookPen size={14} /> Our blog</span>
                    <h2 id="hbl-title">Tips, Stories &amp; <em>Wedding Ideas</em></h2>
                    <p>Advice for families finding a life partner and planning the celebration.</p>
                </header>

                <div className={`hbl__layout ${others.length === 0 ? 'is-single' : ''}`}>
                    <Link to={`/blog/${featured.slug}`} className="hbl-feature">
                        <Media post={featured} className="hbl-feature__media" />
                        <div className="hbl-feature__shade" />
                        <div className="hbl-feature__body">
                            <span className="hbl-pill">{featured.category || 'General'}</span>
                            <h3>{featured.title}</h3>
                            {featured.excerpt && <p>{featured.excerpt}</p>}
                            <div className="hbl-meta">
                                <span><Calendar size={13} /> {formatBlogDate(featured.publishedAt)}</span>
                                <span className="hbl-go">Read article <ArrowUpRight size={16} /></span>
                            </div>
                        </div>
                    </Link>

                    {others.length > 0 && (
                        <div className="hbl__side">
                            {others.map(post => (
                                <Link key={post._id} to={`/blog/${post.slug}`} className="hbl-row">
                                    <Media post={post} className="hbl-row__media" />
                                    <div className="hbl-row__body">
                                        <span className="hbl-pill hbl-pill--soft">{post.category || 'General'}</span>
                                        <h3>{post.title}</h3>
                                        <div className="hbl-meta hbl-meta--dark">
                                            <span><Calendar size={12} /> {formatBlogDate(post.publishedAt)}</span>
                                            <span><Clock size={12} /> {post.excerpt ? `${Math.max(1, Math.round(post.excerpt.split(' ').length / 12))} min read` : 'Quick read'}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                <div className="hbl__more">
                    <Link to="/blog">View all articles <ArrowRight size={16} /></Link>
                </div>
            </div>
        </section>
    );
};

export default BlogSection;
